import React, { useState, useEffect, useRef } from 'react';
import { Camera, Mic } from 'lucide-react';
import EntranceExamQuestions from './EntranceExamQuestions';
import style from './EntranceExam.module.css';
import CommonStyle from "../../../../GlobalComponent/CommonStyle/CommonStyle.module.css";

const NewEntranceExam = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [micStream, setMicStream] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes in seconds
  const videoRef = useRef(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        alert("Warning: Do not switch tabs during the exam!");
      }
    };

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "You have an ongoing exam. Are you sure you want to leave?";
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isFullscreen]);

  useEffect(() => {
    // Disable inspect element, right-click & double-click
    const handleKeyDown = (event) => {
      if (
        event.key === "F12" ||
        (event.ctrlKey && event.shiftKey && event.key === "I") ||
        (event.ctrlKey && event.shiftKey && event.key === "J") ||
        (event.ctrlKey && event.key === "U")
      ) {
        event.preventDefault();
      }
    };

    const handleContextMenu = (event) => event.preventDefault();
    const handleDoubleClick = (event) => event.preventDefault();

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("dblclick", handleDoubleClick);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("dblclick", handleDoubleClick);
    };
  }, []);

  const startExam = async () => {
    try {
      await document.documentElement.requestFullscreen();

      const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(camStream);
      if (videoRef.current) {
        videoRef.current.srcObject = camStream;
      }

      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicStream(micStream);
    } catch (error) {
      console.error('Error starting exam:', error);
      alert('Failed to start exam. Please ensure you have given permission for camera and microphone.');
    }
  };

  const endExam = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    if (micStream) {
      micStream.getTracks().forEach(track => track.stop());
    }
    setCameraStream(null);
    setMicStream(null);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <div className={style.container} style={{ textAlign: "center", padding: "" }}>
      <div className={style.header}>
        <h1 style={{ fontSize: "", marginBottom: "10px" }}>Entrance Exam</h1>
        <p style={{ fontSize: "16px", color: "#555" }}>Please start the exam when you're ready</p>
      </div>
      <div className={style.content}>
        {!isFullscreen ? (
          <div>
            <div style={{ fontSize: "14px", color: "#777", marginBottom: "15px" }}>
              The exam will start in fullscreen mode. Your camera and microphone will be activated.
            </div>
            <button onClick={startExam} className={`${style.common_button_style} ${style.button}`}>
              Start Exam
            </button>
          </div>
        ) : (
          <div>
            <div className={style.statusIndicators}>
              <div className={style.indicator}>
                <Camera size={20} />
                <span className={style.cameraActive}>Camera Active</span>
              </div>
              <div className={style.indicator}>
                <Mic size={20} />
                <span className={style.micActive}>Microphone Active</span>
              </div>
            </div>

            <div className={style.videoContainer}>
              <video ref={videoRef} autoPlay muted className={style.video} />
            </div>

            <div className={style.timer}>
              <div style={{ fontSize: "18px", fontWeight: "bold", marginTop: "15px", color: "#d9534f" }}>
                Time Left: {formatTime(timeLeft)}
              </div>
            </div>

            <div className={style.questions}>
              <EntranceExamQuestions />
            </div>
          </div>
        )}
      </div>
      <div className={style.footer}>
        {isFullscreen && (
          <button onClick={endExam} className={`${style.common_button_style} ${style.button}`}>
            End Exam
          </button>
        )}
      </div>
    </div>
  );
};

export default NewEntranceExam;
